import { useState } from 'react';
import { updateGame } from '../../services/content/gameService';
import { ImageUploadField } from './ImageUploadField';
import type { Game, ProspectProfile } from '../../types/content/game';

interface GameProspectProfileModalProps {
  game: Game;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyProfile: ProspectProfile = {
  name: '', age: '', occupation: '', background: '', key_detail: '', starting_mood: '', imageUrl: '',
};

export function GameProspectProfileModal({ game, onClose, onSuccess }: GameProspectProfileModalProps) {
  const initial = game.prospectProfile || emptyProfile;
  const [name, setName] = useState(initial.name);
  const [age, setAge] = useState(initial.age);
  const [occupation, setOccupation] = useState(initial.occupation);
  const [background, setBackground] = useState(initial.background);
  const [keyDetail, setKeyDetail] = useState(initial.key_detail);
  const [startingMood, setStartingMood] = useState(initial.starting_mood);
  const [imageUrl, setImageUrl] = useState(initial.imageUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      const profile: ProspectProfile = {
        name: name.trim(), age: age.trim(), occupation: occupation.trim(),
        background: background.trim(), key_detail: keyDetail.trim(),
        starting_mood: startingMood.trim(), imageUrl: imageUrl.trim(),
      };
      await updateGame(game.id, { ...game, prospectProfile: profile });
      onSuccess();
    } catch {
      setError('Failed to update prospect profile');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Prospect Profile</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Prospect name" />
              </div>
              <div>
                <label className={labelClass}>Age</label>
                <input type="text" value={age} onChange={(e) => setAge(e.target.value)} className={inputClass} placeholder="e.g. 35" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Occupation</label>
              <input type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Background</label>
              <textarea value={background} onChange={(e) => setBackground(e.target.value)} className={inputClass} rows={3} />
            </div>
            <div>
              <label className={labelClass}>Key Detail</label>
              <textarea value={keyDetail} onChange={(e) => setKeyDetail(e.target.value)} className={inputClass} rows={2} />
            </div>
            <div>
              <label className={labelClass}>Starting Mood</label>
              <input type="text" value={startingMood} onChange={(e) => setStartingMood(e.target.value)} className={inputClass} placeholder="e.g. neutral" />
            </div>
            <ImageUploadField
              label="Prospect Image"
              currentUrl={imageUrl || null}
              uploadEndpoint={`/game/${game.id}/prospect-profile/image`}
              onUploaded={() => onSuccess()}
            />
            {imageUrl && (
              <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">
                {imageUrl}
              </a>
            )}
          </div>
          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
